import { registerPlugin, WebPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * The native audio session (plan doc 17).
 *
 * On iOS, audio only comes out of the speaker at full volume, ignores the
 * ringer switch, and shares the device with the microphone if the session is
 * configured for it — `.playAndRecord` with `.defaultToSpeaker`. That is
 * native code (`ios/App/App/AudioSessionPlugin.swift`); this is its bridge.
 * Android's equivalent asks for transient audio focus so music in another
 * app ducks while a lesson plays.
 *
 * The web has no session to configure. The browser routes audio itself, so
 * the web implementation is a no-op and the call sites never branch.
 */

export type AudioSessionMode =
  /** Before the microphone starts: speaker out, mic in, mixes with others. */
  | 'playAndRecord'
  /** Everything else: playback only, still through the speaker. */
  | 'playback';

export interface AudioInterruption {
  /** True when the interruption starts (a call), false when it ends. */
  began: boolean;
}

export interface AudioSessionPlugin {
  configure(options: { mode: AudioSessionMode }): Promise<void>;
  addListener(
    eventName: 'audioInterrupted',
    listener: (event: AudioInterruption) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

class AudioSessionWeb extends WebPlugin implements AudioSessionPlugin {
  async configure(): Promise<void> {
    // Nothing to do: the browser owns routing.
  }
}

export const AudioSession = registerPlugin<AudioSessionPlugin>('AudioSession', {
  web: () => Promise.resolve(new AudioSessionWeb()),
});
